import React, { useEffect, useReducer } from 'react'
import { styled } from '@mui/material/styles'
import * as XLSX from 'xlsx'
import './style.scss'
import {
    Button,
    Dialog,
    FormControl,
    FormGroup,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
} from '@mui/material'
import { GridSearchIcon } from '@mui/x-data-grid'
import {
    createHearingLecture,
    getHearingLectures,
    updateHearingLecture,
    bulkUploadHearingLectures,
    bulkDeleteHearingLectures,
} from '../../services/hearing-lectures'
import { isMobileScreen, modifyProperty } from '../../utils'
import { ALERT } from '../../constants'
import {
    CheckCircleRounded,
    Edit,
    Visibility,
    CloudDownload,
    CloudUpload,
    Delete,
} from '@mui/icons-material'
import store from '../../store'
import { showAlert } from '../../actions'
import { CustomDataGrid } from '../../components/CustomTable/CustomDataGrid'

const PREFIX = 'HearingLecturesContainer'

const classes = {
    tableHeader: `${PREFIX}-tableHeader`,
    modal: `${PREFIX}-modal`,
    modalRoot: `${PREFIX}-modalRoot`,
    paper: `${PREFIX}-paper`,
}

const Root = styled('div')({
    [`& .${classes.tableHeader}`]: {
        padding: '20px',
        marginBottom: '20px',
    },
    [`& .${classes.modal}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    [`& .${classes.modalRoot}`]: {
        position: 'absolute',
        flexGrow: 1,
        minWidth: 300,
        transform: 'translateZ(0)',
        '@media all and (-ms-high-contrast: none)': {
            display: 'none',
        },
    },
})

const styles = {
    paper: {
        width: 500,
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '40px',
        lineHeight: '1.44',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
}

const getOrdinal = (num) => {
    const n = Number(num)
    if (!n) return ''
    const remHundred = n % 100
    if (remHundred >= 11 && remHundred <= 13) {
        return `${n}th`
    }
    switch (n % 10) {
        case 1:
            return `${n}st`
        case 2:
            return `${n}nd`
        case 3:
            return `${n}rd`
        default:
            return `${n}th`
    }
}

const initialState = {
    data: [],
    form: {
        title: '',
        speaker: '',
        description: '',
        category: '',
        drive_url: '',
        order: '',
    },
    modal: false,
    editMode: false,
    editId: null,
    uploadFile: null,
    selectedIds: [],
    filters: {
        pageSize: 10,
        page: 0,
        total_results: 0,
        total_pages: 0,
        search: '',
        category: '',
    },
}

function HearingLecturesContainer() {
    const [
        {
            data,
            modal,
            form,
            loading,
            filters,
            editMode,
            editId,
            uploadFile,
            selectedIds,
        },
        setState,
    ] = useReducer(
        (prev, data) => ({
            ...prev,
            ...data,
            filters: { ...prev.filters, ...data.filters },
            form: { ...prev.form, ...data.form },
        }),
        initialState
    )

    const isMobile = isMobileScreen()
    const fileInputRef = React.useRef(null)

    const fetchData = () => {
        const params = {
            page: filters.page + 1,
            per_page: filters.pageSize,
            search: filters.search,
            category: filters.category,
        }
        setState({ loading: true })
        getHearingLectures(params)
            .then((res) => {
                const { data, total_pages, total_results } = res?.data || {}
                setState({
                    data: data || [],
                    loading: false,
                    selectedIds: [],
                    filters: {
                        total_results: total_results || 0,
                        total_pages: total_pages || 0,
                    },
                })
            })
            .catch((error) => {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message: 'Failed to fetch hearing lectures',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            })
    }

    useEffect(() => {
        fetchData()
    }, [
        filters.search,
        filters.category,
        filters.page,
        filters.pageSize,
    ])

    const rowWidthConfig = {
        order: { default: { flex: 0.08 }, mobile: { width: 60 } },
        year: { default: { flex: 0.1 }, mobile: { width: 90 } },
        month: { default: { flex: 0.12 }, mobile: { width: 110 } },
        week: { default: { flex: 0.1 }, mobile: { width: 90 } },
        title: { default: { flex: 0.22 }, mobile: { width: 150 } },
        speaker: { default: { flex: 0.15 }, mobile: { width: 120 } },
        category: { default: { flex: 0.12 }, mobile: { width: 120 } },
        description: { default: { flex: 0.21 }, mobile: { width: 150 } },
        action: { default: { flex: 0.15 }, mobile: { width: 120 } },
    }

    const headCells = [
        {
            field: 'order',
            numeric: true,
            headerName: 'Order',
            sortable: true,
        },
        {
            field: 'year',
            numeric: true,
            headerName: 'Year',
            sortable: false,
            renderCell: (params) => {
                const value = getOrdinal(params.row.year)
                return value ? `${value} Year` : ''
            },
        },
        {
            field: 'month',
            numeric: true,
            headerName: 'Month',
            sortable: false,
            renderCell: (params) => {
                const monthNames = [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                ]
                const idx = Number(params.row.month) - 1
                return monthNames[idx] || params.row.month
            },
        },
        {
            field: 'week',
            numeric: true,
            headerName: 'Week',
            sortable: false,
            renderCell: (params) => {
                const value = getOrdinal(params.row.week)
                return value ? `${value} Week` : ''
            },
        },
        {
            field: 'title',
            numeric: false,
            headerName: 'Title',
            sortable: false,
        },
        {
            field: 'speaker',
            numeric: false,
            headerName: 'Speaker',
            sortable: false,
        },
        {
            field: 'category',
            numeric: false,
            headerName: 'Category',
            sortable: false,
        },
        {
            field: 'description',
            numeric: false,
            headerName: 'Description',
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {params.row.description}
                    </div>
                )
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            renderCell: (params) => {
                return (
                    <>
                        {params.row.drive_url && (
                            <IconButton
                                onClick={() => {
                                    window.open(
                                        params.row.drive_url,
                                        '_blank'
                                    )
                                }}
                                aria-label="View"
                                color="primary"
                                title="View"
                                size="large"
                            >
                                <Visibility />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={() => handleEdit(params.row)}
                            aria-label="Edit"
                            color="primary"
                            title="Edit"
                            size="large"
                        >
                            <Edit />
                        </IconButton>
                    </>
                )
            },
        },
    ]

    const onFilterChange = (name, value) => {
        setState({
            filters: {
                [name]: value,
                page: 0,
            },
        })
    }

    const onPageSizeChange = ({ page, pageSize }) => {
        setState({
            filters: {
                page,
                pageSize,
            },
        })
    }

    const handleSelectionChange = (newSelection) => {
        setState({ selectedIds: newSelection })
    }

    const handleInput = (name, value) => {
        setState({
            form: {
                [name]: value,
            },
        })
    }

    const handleEdit = (row) => {
        setState({
            modal: true,
            editMode: true,
            editId: row.id,
            form: {
                title: row.title || '',
                speaker: row.speaker || '',
                description: row.description || '',
                category: row.category || '',
                drive_url: row.drive_url || '',
                order: row.order || '',
            },
        })
    }

    const validateForm = () => {
        const { title, speaker, drive_url, order } = form

        if (!title || !title.trim()) {
            store.dispatch(
                showAlert({
                    message: 'Title is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!speaker || !speaker.trim()) {
            store.dispatch(
                showAlert({
                    message: 'Speaker is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!drive_url || !drive_url.trim()) {
            store.dispatch(
                showAlert({
                    message: 'Drive URL is required',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        if (!order || order <= 0) {
            store.dispatch(
                showAlert({
                    message: 'Order must be a positive number',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return false
        }

        return true
    }

    const handleSubmit = () => {
        if (!validateForm()) {
            return
        }

        const body = {
            title: form.title,
            speaker: form.speaker,
            description: form.description || '',
            category: form.category || '',
            drive_url: form.drive_url,
            order: parseInt(form.order),
        }

        setState({ loading: true })

        const apiCall = editMode
            ? updateHearingLecture(editId, body)
            : createHearingLecture(body)

        apiCall
            .then(() => {
                store.dispatch(
                    showAlert({
                        message: `Lecture ${editMode ? 'updated' : 'created'} successfully`,
                        severity: ALERT.SUCCESS,
                        duration: 3000,
                    })
                )
                fetchData()
                setState({
                    modal: false,
                    loading: false,
                    editMode: false,
                    editId: null,
                    form: {
                        title: '',
                        speaker: '',
                        description: '',
                        category: '',
                        drive_url: '',
                        order: '',
                    },
                })
            })
            .catch((error) => {
                setState({ loading: false })
            })
    }

    const handleCloseModal = () => {
        setState({
            modal: false,
            editMode: false,
            editId: null,
            form: {
                title: '',
                speaker: '',
                description: '',
                category: '',
                drive_url: '',
                order: '',
            },
        })
    }

    const downloadTemplate = () => {
        const headers = [
            'year',
            'month',
            'week',
            'title',
            'speaker',
            'description',
            'category',
            'drive_url',
        ]

        // Generate sample data for first 2 weeks (14 lectures)
        const sampleRows = []
        for (let week = 1; week <= 2; week++) {
            for (let day = 1; day <= 7; day++) {
                sampleRows.push(
                    `1,1,${week},Lecture ${(week - 1) * 7 + day},Sample Speaker,Description for lecture ${(week - 1) * 7 + day},Philosophy,https://drive.google.com/sample-${(week - 1) * 7 + day}`
                )
            }
        }

        const csvContent = headers.join(',') + '\n' + sampleRows.join('\n')

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', 'hearing_lectures_template.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        store.dispatch(
            showAlert({
                message:
                    'Template downloaded successfully (Year/Month/Week structure with 14 sample lectures)',
                severity: ALERT.SUCCESS,
                duration: 3000,
            })
        )
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            const validTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ]
            if (!validTypes.includes(file.type)) {
                store.dispatch(
                    showAlert({
                        message:
                            'Invalid file type. Please upload CSV or Excel file.',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
                return
            }
            setState({ uploadFile: file })
        }
    }

    const calculateOrder = (year, month, week) => {
        // Calculate week number within all years
        // 12 months per year, 4 weeks per month = 48 weeks per year
        const weekNumber = (year - 1) * 48 + (month - 1) * 4 + week
        // Each week has 7 lectures, calculate starting order for this week
        const startOrder = (weekNumber - 1) * 7 + 1
        return { weekNumber, startOrder }
    }

    const handleBulkUpload = () => {
        if (!uploadFile) {
            store.dispatch(
                showAlert({
                    message: 'Please select a file to upload',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return
        }

        setState({ loading: true })

        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json(worksheet)

                // Group lectures by year, month, week
                const groupedLectures = {}
                jsonData.forEach((row) => {
                    const year = parseInt(row.year) || 1
                    const month = parseInt(row.month) || 1
                    const week = parseInt(row.week) || 1

                    // Validate year, month, week ranges
                    if (year < 1 || month < 1 || month > 12 || week < 1 || week > 4) {
                        return // Skip invalid entries
                    }

                    const key = `${year}-${month}-${week}`
                    if (!groupedLectures[key]) {
                        groupedLectures[key] = {
                            year,
                            month,
                            week,
                            lectures: [],
                        }
                    }

                    groupedLectures[key].lectures.push({
                        title: row.title || '',
                        speaker: row.speaker || '',
                        description: row.description || '',
                        category: row.category || '',
                        drive_url: row.drive_url || '',
                    })
                })

                // Calculate orders and flatten
                const lectures = []
                Object.values(groupedLectures).forEach((group) => {
                    const { startOrder } = calculateOrder(
                        group.year,
                        group.month,
                        group.week
                    )

                    group.lectures.forEach((lecture, index) => {
                        if (
                            lecture.title &&
                            lecture.speaker &&
                            lecture.drive_url
                        ) {
                            lectures.push({
                                ...lecture,
                                order: startOrder + index,
                            })
                        }
                    })
                })

                if (lectures.length === 0) {
                    store.dispatch(
                        showAlert({
                            message:
                                'No valid lecture entries found in the file',
                            severity: ALERT.ERROR,
                            duration: 5000,
                        })
                    )
                    setState({ loading: false })
                    return
                }

                bulkUploadHearingLectures(lectures)
                    .then((res) => {
                        store.dispatch(
                            showAlert({
                                message:
                                    res?.message ||
                                    `${lectures.length} lecture(s) uploaded successfully`,
                                severity: ALERT.SUCCESS,
                                duration: 3000,
                            })
                        )
                        setState({ uploadFile: null, loading: false })
                        if (fileInputRef.current) {
                            fileInputRef.current.value = ''
                        }
                        fetchData()
                    })
                    .catch((error) => {
                        setState({ loading: false })
                        store.dispatch(
                            showAlert({
                                message:
                                    error?.response?.data?.message ||
                                    'Failed to upload lectures',
                                severity: ALERT.ERROR,
                                duration: 5000,
                            })
                        )
                    })
            } catch (error) {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message: 'Failed to parse file. Please check the format.',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            }
        }
        reader.onerror = () => {
            setState({ loading: false })
            store.dispatch(
                showAlert({
                    message: 'Failed to read file',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
        }
        reader.readAsArrayBuffer(uploadFile)
    }

    const handleBulkDelete = () => {
        if (!selectedIds.length) {
            store.dispatch(
                showAlert({
                    message: 'Please select at least one record to delete',
                    severity: ALERT.ERROR,
                    duration: 5000,
                })
            )
            return
        }

        const proceed = window.confirm(
            `Are you sure you want to delete ${selectedIds.length} record(s)?`
        )

        if (!proceed) {
            return
        }

        setState({ loading: true })
        bulkDeleteHearingLectures(selectedIds)
            .then((res) => {
                store.dispatch(
                    showAlert({
                        message:
                            res?.message ||
                            `${selectedIds.length} record(s) deleted successfully`,
                        severity: ALERT.SUCCESS,
                        duration: 3000,
                    })
                )
                fetchData()
            })
            .catch((error) => {
                setState({ loading: false })
                store.dispatch(
                    showAlert({
                        message:
                            error?.response?.data?.message ||
                            'Failed to delete selected records',
                        severity: ALERT.ERROR,
                        duration: 5000,
                    })
                )
            })
    }

    return (
        <Root className="hearing-lectures-container">
            <Paper className="p-3">
                <div className="table-header-container">
                    <div className="filter-header">
                        <TextField
                            id="input-with-icon-textfield"
                            label="Search"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <GridSearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            name="search"
                            onChange={(e) =>
                                onFilterChange(e.target.name, e.target.value)
                            }
                            autoComplete="off"
                            value={filters.search}
                            className={'search-field'}
                        />

                        <TextField
                            label="Category"
                            variant="outlined"
                            name="category"
                            value={filters.category}
                            onChange={(e) =>
                                onFilterChange(e.target.name, e.target.value)
                            }
                            className={'category-field'}
                        />

                        <Button
                            onClick={() => setState({ modal: true })}
                            color="primary"
                            variant="contained"
                            size="large"
                        >
                            Add Lecture
                        </Button>
                        <Button
                            onClick={handleBulkDelete}
                            color="error"
                            variant="outlined"
                            size="large"
                            startIcon={<Delete />}
                            disabled={!selectedIds.length || loading}
                        >
                            Delete Selected
                        </Button>
                    </div>
                </div>
            </Paper>
            <Paper className="p-3 m-top-16">
                <div className="bulk-upload-container">
                    <h3>Bulk Upload</h3>
                    <div className="bulk-upload-actions">
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CloudDownload />}
                            onClick={downloadTemplate}
                            size="large"
                        >
                            Download Template
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current?.click()}
                            size="large"
                        >
                            Select File
                        </Button>
                        {uploadFile && (
                            <span className="file-name">
                                {uploadFile.name}
                            </span>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBulkUpload}
                            disabled={!uploadFile || loading}
                            size="large"
                        >
                            Upload
                        </Button>
                    </div>
                </div>
            </Paper>
            <div style={{ background: 'white' }}>
                <CustomDataGrid
                    rows={modifyProperty(data, '_id', 'id').map((row) => {
                        const order = Number(row.order)
                        if (!order || isNaN(order)) return row
                        const weekNumber = Math.floor((order - 1) / 7) + 1
                        const year = Math.floor((weekNumber - 1) / 48) + 1
                        const remainingWeeks = (weekNumber - 1) % 48
                        const month = Math.floor(remainingWeeks / 4) + 1
                        const week = (remainingWeeks % 4) + 1
                        return { ...row, year, month, week }
                    })}
                    columns={headCells.map((col) => ({
                        ...col,
                        sortable: col.field === 'order',
                        disableClickEventBubbling: true,
                        ...(isMobile
                            ? rowWidthConfig[col.field].mobile ||
                            rowWidthConfig[col.field].default
                            : rowWidthConfig[col.field].default),
                    }))}
                    rowCount={filters.total_results}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: filters.pageSize,
                            },
                        },
                    }}
                    disableColumnFilter
                    loading={loading}
                    paginationModel={filters}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedIds}
                    onRowSelectionModelChange={handleSelectionChange}
                    onPaginationModelChange={(page) =>
                        onPageSizeChange({
                            pageSize: page.pageSize,
                            page:
                                page.pageSize != filters.pageSize
                                    ? 0
                                    : page.page,
                        })
                    }
                />
            </div>
            <Dialog
                open={!!modal}
                onClose={handleCloseModal}
                className={classes.modal}
            >
                <div style={styles.paper}>
                    <h1>{editMode ? 'Update Lecture' : 'Create Lecture'}</h1>
                    <FormGroup row={1} sx={{ width: '100%' }}>
                        <TextField
                            className="m-top-16"
                            label="Title"
                            variant="outlined"
                            value={form.title}
                            required
                            type="text"
                            name="title"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Speaker"
                            variant="outlined"
                            value={form.speaker}
                            required
                            type="text"
                            name="speaker"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Category"
                            variant="outlined"
                            value={form.category}
                            type="text"
                            name="category"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Drive URL"
                            variant="outlined"
                            value={form.drive_url}
                            required
                            type="url"
                            name="drive_url"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Order"
                            variant="outlined"
                            value={form.order}
                            required
                            type="number"
                            name="order"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            fullWidth
                        />
                        <TextField
                            className="m-top-16"
                            label="Description"
                            variant="outlined"
                            value={form.description}
                            type="text"
                            name="description"
                            onChange={(e) =>
                                handleInput(e.target.name, e.target.value)
                            }
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </FormGroup>
                    <div style={{ marginTop: '16px' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CheckCircleRounded />}
                            onClick={handleSubmit}
                            aria-label="Submit"
                            style={{ marginRight: '12px' }}
                            disabled={loading}
                        >
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCloseModal}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Dialog>
        </Root>
    )
}

export default HearingLecturesContainer

